/**
 * Pure derivation logic for the composite view's per-station age strip and hero
 * counter. Kept dependency-free and testable, same spirit as ageDisplay.ts.
 */
import type { HotManifest } from "./types";
import type { ContributingStation } from "./mosaicTypes";

export type DotState = "fresh" | "aging" | "stale";

/** Composite strip thresholds: green <90s, amber <240s, red beyond or missing.
 * Deliberately looser than the single-station hero's 60/180 (ageDisplay.ts) —
 * with 5 independent stations on staggered 10s polls, the strip should read
 * "healthy" without flickering amber on ordinary poll jitter. */
export function stripDotState(ageSec: number | null): DotState {
  if (ageSec === null) return "stale";
  if (ageSec < 90) return "fresh";
  if (ageSec < 240) return "aging";
  return "stale";
}

export interface StationStripEntry {
  id: string;
  /** null when the station has never reported a manifest (offline / not yet active). */
  ageSec: number | null;
  dotState: DotState;
  bakeLatencyMs: number | null;
}

/** One entry per station id, in the given order, from each station's latest known manifest. */
export function deriveStationStrip(
  stationIds: readonly string[],
  manifests: ReadonlyMap<string, HotManifest | null>,
  nowMs: number,
): StationStripEntry[] {
  return stationIds.map((id) => {
    const latest = manifests.get(id)?.latest ?? null;
    const ageSec = latest ? (nowMs - latest.dataTimeLastMs) / 1000 : null;
    const bakeLatencyMs = latest ? latest.bakeTimeMs - latest.dataTimeLastMs : null;
    return { id, ageSec, dotState: stripDotState(ageSec), bakeLatencyMs };
  });
}

/** Mosaic-mode variant of deriveStationStrip: driven by the CURRENT mosaic
 * frame's own `contributing` list (workers/CONTRACTS.md's Mosaic-head
 * section) instead of N independent per-station manifests. A station in
 * `stationIds` (the fleet roster) but absent from `contributing` is
 * offline/dropped out (MAX_CONTRIB_AGE_MS, a stale beam, etc.) — same visual
 * treatment (null age, stale dot) as deriveStationStrip's "never reported"
 * case.
 *
 * `ageMs` per contributing station is a snapshot as of the frame's own
 * `frameBakeTimeMs` (mosaicTypes.ts's doc comment) — projected forward by
 * elapsed wall time since then so the strip keeps ticking between manifest
 * polls, the same live-derivation-from-a-captured-timestamp approach
 * ageDisplay.ts/deriveStationStrip already use for per-station ages.
 * `bakeLatencyMs` is always null here — the contributing list doesn't carry
 * a per-station bake latency, only age (app.ts shows the mosaic FRAME's own
 * compose latency in the hero panel instead, a more meaningful number than
 * any single contributing station's). */
export function deriveMosaicStationStrip(
  stationIds: readonly string[],
  contributing: readonly ContributingStation[],
  frameBakeTimeMs: number | null,
  nowMs: number,
): StationStripEntry[] {
  const byStation = new Map(contributing.map((c) => [c.station, c]));
  const elapsedSinceBakeMs = frameBakeTimeMs !== null ? Math.max(0, nowMs - frameBakeTimeMs) : 0;
  return stationIds.map((id) => {
    const c = byStation.get(id);
    const ageSec = c ? (c.ageMs + elapsedSinceBakeMs) / 1000 : null;
    return { id, ageSec, dotState: stripDotState(ageSec), bakeLatencyMs: null };
  });
}

/** The freshest (lowest-age) reporting station; null if none are reporting. */
export function pickFreshest(strip: readonly StationStripEntry[]): StationStripEntry | null {
  let best: StationStripEntry | null = null;
  let bestAge = Infinity;
  for (const entry of strip) {
    if (entry.ageSec === null) continue;
    if (entry.ageSec < bestAge) {
      bestAge = entry.ageSec;
      best = entry;
    }
  }
  return best;
}
